import React, { useState } from 'react';
import { Icons } from '../Icons';
import { Patient } from '../../types';
import { formatAuditTimestamp, getActionColor, formatFileSize } from '../../utils/auditLog';

interface PatientModalProps {
  isOpen: boolean;
  editingPatient: Patient | null;
  patientForm: Omit<Patient, 'id'>;
  onClose: () => void;
  onSave: (e: React.FormEvent) => void;
  onFormChange: (form: Omit<Patient, 'id'>) => void;
  contactOnly?: boolean;
}

export const PatientModal: React.FC<PatientModalProps> = ({
  isOpen,
  editingPatient,
  patientForm,
  onClose,
  onSave,
  onFormChange,
  contactOnly = false,
}) => {
  const [alertInput, setAlertInput] = useState('');
  const [activeTab, setActiveTab] = useState<'info' | 'history' | 'attachments' | 'audit'>('info');

  if (!isOpen) return null;

  const handleAddAlert = () => {
    if (alertInput.trim()) {
      onFormChange({ ...patientForm, alerts: [...patientForm.alerts, alertInput.trim()] });
      setAlertInput('');
    }
  };

  const handleRemoveAlert = (alert: string) => {
    onFormChange({ ...patientForm, alerts: patientForm.alerts.filter(a => a !== alert) });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newAttachments = Array.from(files).map(file => ({
      id: `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type.includes('pdf') ? 'pdf' as const :
            file.type.includes('image') ? 'xray' as const : 'other' as const,
      url: URL.createObjectURL(file),
      uploadedAt: new Date().toISOString(),
      uploadedBy: 'Current User',
      size: file.size,
    }));

    onFormChange({
      ...patientForm,
      attachments: [...patientForm.attachments, ...newAttachments],
    });
  };

  const handleRemoveAttachment = (attachmentId: string) => {
    onFormChange({
      ...patientForm,
      attachments: patientForm.attachments.filter(a => a.id !== attachmentId),
    });
  };

  return (
    <div className="fixed inset-0 z-[400] bg-slate-900/60 backdrop-blur-md flex items-center justify-center p-6 animate-fadeIn">
      <div className="bg-white rounded-[3rem] w-full max-w-2xl shadow-2xl my-6 max-h-[90vh] flex flex-col animate-slideUp">
        <div className="p-8 border-b bg-white rounded-t-[3rem] flex-shrink-0">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-2xl font-black">{editingPatient ? 'Edit Patient' : 'New Patient'}</h3>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-red-500">
              <Icons.X />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex bg-slate-100 rounded-xl p-1">
            {[
              { id: 'info', label: 'Patient Info', icon: Icons.Users },
              { id: 'history', label: 'Dental History', icon: Icons.FileText },
              { id: 'attachments', label: 'Attachments', icon: Icons.Paperclip },
              { id: 'audit', label: 'Audit Log', icon: Icons.Clock },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-bold capitalize transition-all ${
                  activeTab === tab.id ? 'bg-white shadow-sm' : 'text-slate-500'
                }`}
              >
                <tab.icon />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={onSave} className="p-8 space-y-6 overflow-y-auto flex-1">
          {/* Patient Info Tab */}
          {activeTab === 'info' && (
            <div className="grid grid-cols-2 gap-4 animate-fadeIn">
              <div className="col-span-2">
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Full Name *</label>
                <input
                  required
                  type="text"
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={patientForm.name}
                  onChange={e => onFormChange({ ...patientForm, name: e.target.value })}
                  disabled={contactOnly}
                />
              </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Phone *</label>
              <input
                required
                type="tel"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={patientForm.phone}
                onChange={e => onFormChange({ ...patientForm, phone: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Email *</label>
              <input
                required
                type="email"
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={patientForm.email}
                onChange={e => onFormChange({ ...patientForm, email: e.target.value })}
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">DOB *</label>
                <input
                  required
                  type="date"
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={patientForm.dob}
                  onChange={e => onFormChange({ ...patientForm, dob: e.target.value })}
                  disabled={contactOnly}
                />
              </div>

            <div>
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Gender</label>
              <select
                className="w-full border-2 rounded-xl p-3 font-bold"
                value={patientForm.gender}
                onChange={e => onFormChange({ ...patientForm, gender: e.target.value as 'Male' | 'Female' | 'Non-binary' })}
                disabled={contactOnly}
              >
                <option>Male</option>
                <option>Female</option>
                <option>Non-binary</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Address</label>
                <input
                  type="text"
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  value={patientForm.address}
                  onChange={e => onFormChange({ ...patientForm, address: e.target.value })}
                  disabled={false}
                />
              </div>

            <div className="col-span-2">
              <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Insurance</label>
                <input
                  type="text"
                  className="w-full border-2 rounded-xl p-3 font-bold"
                  placeholder="Optional - leave blank for self-pay"
                  value={patientForm.insurance}
                  onChange={e => onFormChange({ ...patientForm, insurance: e.target.value })}
                  disabled={contactOnly}
                />
              </div>

            {/* Alerts */}
            <div className="col-span-2 p-4 bg-red-50 rounded-2xl">
              <label className="text-xs font-bold text-red-600 uppercase mb-2 block">Medical Alerts</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {patientForm.alerts.map(a => (
                  <span key={a} className="flex items-center bg-red-600 text-white px-3 py-1 rounded-xl text-xs font-bold">
                    {a}
                    {!contactOnly && (
                      <button type="button" onClick={() => handleRemoveAlert(a)} className="ml-2">
                        <Icons.X />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {!contactOnly && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 border-2 rounded-xl p-2 text-sm"
                    placeholder="Add alert..."
                    value={alertInput}
                    onChange={e => setAlertInput(e.target.value)}
                    onKeyPress={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddAlert();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddAlert}
                    className="px-4 py-2 bg-red-600 text-white rounded-xl text-xs font-bold"
                  >
                    Add
                  </button>
                </div>
              )}
            </div>

            {/* Emergency Contact */}
            <div className="col-span-2 p-4 bg-blue-50 rounded-2xl">
              <label className="text-xs font-bold text-blue-600 uppercase mb-3 block">Emergency Contact</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                    className="w-full border-2 rounded-xl p-2 text-sm font-bold"
                    placeholder="Name"
                    value={patientForm.emergencyContact.name}
                    onChange={e => onFormChange({
                      ...patientForm,
                      emergencyContact: { ...patientForm.emergencyContact, name: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    className="w-full border-2 rounded-xl p-2 text-sm font-bold"
                    placeholder="Phone"
                    value={patientForm.emergencyContact.phone}
                    onChange={e => onFormChange({
                      ...patientForm,
                      emergencyContact: { ...patientForm.emergencyContact, phone: e.target.value }
                    })}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    className="w-full border-2 rounded-xl p-2 text-sm font-bold"
                    placeholder="Relationship"
                    value={patientForm.emergencyContact.relationship}
                    onChange={e => onFormChange({
                      ...patientForm,
                      emergencyContact: { ...patientForm.emergencyContact, relationship: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Dental History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4 animate-fadeIn">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase mb-2 block">Dental History Notes</label>
                <textarea
                  className="w-full border-2 rounded-xl p-3 font-medium min-h-[300px]"
                  placeholder="Enter dental history, past procedures, treatment notes, preferences, etc."
                  value={patientForm.dentalHistory}
                  onChange={e => onFormChange({ ...patientForm, dentalHistory: e.target.value })}
                  readOnly={contactOnly}
                />
              </div>
              {editingPatient && patientForm.lastVisit && (
                <div className="p-4 bg-blue-50 rounded-2xl">
                  <div className="flex items-center space-x-2">
                    <Icons.Calendar />
                    <div>
                      <p className="text-xs font-bold text-blue-600 uppercase">Last Visit</p>
                      <p className="font-bold text-blue-900">{new Date(patientForm.lastVisit).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Attachments Tab */}
          {activeTab === 'attachments' && (
            <div className="space-y-4 animate-fadeIn">
              <div className="p-4 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
                {contactOnly ? (
                  <p className="text-sm text-slate-600 text-center">Attachments are view-only for patients.</p>
                ) : (
                  <label className="flex flex-col items-center cursor-pointer">
                    <div className="p-4 bg-blue-100 rounded-full text-blue-600 mb-3">
                      <Icons.Upload />
                    </div>
                    <p className="font-bold text-slate-700 mb-1">Upload X-rays or PDFs</p>
                    <p className="text-xs text-slate-500 mb-3">Click to select files or drag and drop</p>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf"
                      multiple
                      onChange={handleFileUpload}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700"
                      onClick={(e) => {
                        e.preventDefault();
                        (e.currentTarget.previousElementSibling as HTMLInputElement)?.click();
                      }}
                    >
                      Select Files
                    </button>
                  </label>
                )}
              </div>

              {/* Attachments List */}
              {patientForm.attachments.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-slate-400 uppercase">Uploaded Files ({patientForm.attachments.length})</h4>
                  {patientForm.attachments.map(attachment => (
                    <div
                      key={attachment.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border"
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`p-3 rounded-xl ${
                          attachment.type === 'xray' ? 'bg-purple-100 text-purple-600' :
                          attachment.type === 'pdf' ? 'bg-red-100 text-red-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {attachment.type === 'xray' ? <Icons.Image /> :
                           attachment.type === 'pdf' ? <Icons.FileText /> :
                           <Icons.Paperclip />}
                        </div>
                        <div>
                          <p className="font-bold text-sm">{attachment.name}</p>
                          <p className="text-xs text-slate-500">
                            {formatFileSize(attachment.size)} • Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-slate-400">By {attachment.uploadedBy}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <a
                          href={attachment.url}
                          download={attachment.name}
                          className="p-2 hover:bg-blue-100 rounded-lg text-blue-600"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Icons.Download />
                        </a>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(attachment.id)}
                          className="p-2 hover:bg-red-100 rounded-lg text-red-600"
                        >
                          <Icons.Trash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Audit Log Tab */}
          {activeTab === 'audit' && (
            <div className="space-y-4 animate-fadeIn">
              {patientForm.auditLog.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <Icons.Clock />
                  <p className="font-bold mt-2">No audit history yet</p>
                  <p className="text-xs">Changes to this patient record will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patientForm.auditLog.map(entry => (
                    <div
                      key={entry.id}
                      className="p-4 bg-slate-50 rounded-2xl border-l-4"
                      style={{ borderLeftColor: getActionColor(entry.action).split(' ')[0] }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className={`inline-block px-2 py-1 rounded-lg text-xs font-bold ${getActionColor(entry.action)}`}>
                            {entry.action.toUpperCase()}
                          </span>
                          <p className="font-bold text-sm mt-1">{entry.description}</p>
                        </div>
                        <p className="text-xs text-slate-500">{formatAuditTimestamp(entry.timestamp)}</p>
                      </div>
                      <p className="text-xs text-slate-500">
                        By <span className="font-bold">{entry.userName}</span>
                      </p>
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="mt-3 pt-3 border-t space-y-1">
                          {entry.changes.map((change, idx) => (
                            <div key={idx} className="text-xs">
                              <span className="font-bold text-slate-600">{change.field}:</span>
                              <span className="text-red-600 line-through ml-2">{change.oldValue}</span>
                              <span className="mx-2">→</span>
                              <span className="text-green-600 font-bold">{change.newValue}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="pt-4 border-t sticky bottom-0 bg-white -mx-8 px-8 -mb-8 pb-8 rounded-b-[3rem]">
            <button
              type="submit"
              className="w-full py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700"
            >
              {editingPatient ? 'Save Changes' : 'Create Patient'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
